<template>

  <span
      v-if="role === 'button'"
      class="c-btn"
      :class="getClasses"
      :disabled="disabled"
      @click="onClick"
      tabindex="0"
      @keyup.enter="onClick"
      @keyup.space="onClick">
    <slot v-if="hasLink"></slot>
    <a v-if="!hasLink && !to">
      <slot></slot>
    </a>
    <router-link v-if="!hasLink && to" :to="to">
      <slot></slot>
    </router-link>
  </span>

    <button
        v-else-if="role === 'submit'"
        class="c-btn"
        type="submit"
        :class="getClasses"
        :disabled="disabled"
        @click="onClick">
    <span>
      <slot></slot>
    </span>
    </button>

</template>


<script>

export default {

    name: 'VBtn',

    props: {
        hasLink: Boolean,
        to: [String, Object],
        type: String,
        size: String,
        icon: String,
        disabled: Boolean,
        hasLoader: Boolean,
        loading: Boolean,
        role: {
            type: String,
            default: 'button'
        }
    },

    computed: {
        getClasses() {
            return {
                ['c-btn--size-' + this.size]: this.size,
                ['c-btn--' + this.type]: this.type,
                ['c-btn--icon']: this.icon,
                ['c-btn--' + this.icon]: this.icon,
                ['c-btn--has-loader']: this.hasLoader,
                ['c-btn--loading']: this.loading
            }
        }
    },

    methods: {
        onClick(e) {
            this.$emit("click", e)
        }
    }

}

</script>


<style lang="scss">

@import '~Style/_utils.scss';

.c-btn {
    position: relative;
    display: inline-block;
    border-radius: .25rem;
    color: $c-text;
    border: none;
    box-shadow: none;
    padding: 0;
    background-color: transparent;
    outline: 0;
    overflow: hidden;
    transition: box-shadow .1s ease, background-color .1s ease;
    cursor: pointer;
    flex-shrink: 0;
    flex-grow: 0;
    text-align: center;

    & > a,
    & > span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        line-height: 1;
        padding: 0.7rem 2rem;
        font-size: 1rem;
        font-weight: bold;
        transition: color .1s ease-in-out;

        @include bp($screen-sm) {
            padding: 0.7rem 1.5rem;
        }
    }
    &:hover {
        outline: 0 !important;
    }
    &--has-loader {
        &:before,
        &:after {
            content: '';
            position: absolute;
            opacity: 0;
            transition: opacity .3s ease, visibility 0s .3s linear;
            pointer-events: none;
            visibility: hidden;
        }
        &:before {
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: $c-primary;
        }
        &:after {
            top: calc(50% - 0.8rem);
            left: calc(50% - 0.8rem);
            width: 1.6rem;
            height: 1.6rem;
            background-image: url('#{$icons}/loader.svg');
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            transform: rotate(0deg);
            animation: btn-loading 1s linear infinite;

            @keyframes btn-loading {
                100% {
                    transform: rotate(360deg);
                }
            }
        }
    }
    &--loading {
        pointer-events: none;

        &:before,
        &:after {
            transition-delay: 0s;
            visibility: visible;
            opacity: 1;
        }

        //Sizes
    }
    &--size-small {
        a,
        span {
            border-radius: 0.25rem;
            font-size: 0.9rem;
            padding: 0.5rem 1rem;
        }

        //Types
    }
    &--green {
        background-color: $c-primary;

        a,
        span {
            color: $c-white;
        }
        &:hover {
            box-shadow: inset 0 0 0 1px $c-primary;
            background-color: transparent;

            a,
            span {
                color: $c-primary;
            }
        }
    }
    &--red {
        background-color: $c-red;

        a,
        span {
            color: $c-white;
        }
        &:hover {
            box-shadow: inset 0 0 0 1px $c-red;
            background-color: transparent;

            a,
            span {
                color: $c-red;
            }
        }
        &:before {
            background-color: $c-red;
        }
    }
    &--red-white {
        box-shadow: inset 0 0 0 1px $c-red;
        background-color: transparent;

        a,
        span {
            color: $c-red;
        }
        &:hover {
            box-shadow: none;
            background-color: $c-red;

            a,
            span {
                color: $c-white;
            }
        }
    }
    &--white {
        box-shadow: inset 0 0 0 1px $c-primary;
        background-color: transparent;

        a,
        span {
            color: $c-primary;
        }
        &:hover {
            box-shadow: none;
            background-color: $c-primary;

            a,
            span {
                color: $c-white;
            }
        }
    }
    &--prev {
        box-shadow: inset 0 0 0 1px $c-primary;
        background-color: transparent;
        background-image: url('#{$icons}/arrow-left-green.svg');
        background-repeat: no-repeat;
        background-position: 1.6rem center;
        background-size: 0.45rem 0.7rem;
        transition: background-position .2s ease-in-out;

        a,
        span {
            color: $c-primary;
            padding-left: 3rem;
        }
        &:hover {
            background-position: 1.4rem center;

            a,
            span {
                color: $c-primary;
            }
        }
    }
    &--back {
        background-color: $c-primary;
        background-image: url('#{$icons}/back-arrow.svg');
        background-repeat: no-repeat;
        background-position: 1.4rem center;
        background-size: 0.75rem 0.75rem;
        transition: background-position .2s ease-in-out;

        a,
        span {
            color: $c-white;
            padding-left: 3rem;
        }
        &:hover {
            background-position: 1.2rem center;

            a,
            span {
                color: $c-white;
            }
        }
        @include bp($screen-md) {
            background-size: 1rem 1rem;
            background-position: center center;

            &:hover {
                background-position: 0.5rem center;
            }
            a {
                width: 2.4rem;
                height: 2.4rem;
                padding: 0;
                text-indent: -9999px;
            }
        }
    }
    &--next {
        background-color: $c-primary;
        background-image: url('#{$icons}/arrow-right-white.svg');
        background-repeat: no-repeat;
        background-position: right 1.6rem center;
        background-size: 0.45rem 0.7rem;
        transition: background-position .2s ease-in-out;

        a,
        span {
            color: $c-white;
            padding-right: 3rem;
        }
        &:hover {
            background-position: right 1.4rem center;

            a,
            span {
                color: $c-white;
            }
        }
    }
    &--white-red {
        box-shadow: inset 0 0 0 1px $c-red;
        background-color: transparent;

        a,
        span {
            color: $c-red;
        }
        &:hover {
            background-color: $c-red;
            box-shadow: none;

            a,
            span {
                color: $c-white;
            }
        }
    }
    &--icon {
        background-color: transparent;
        background-position: center;
        background-repeat: no-repeat;
        background-size: 50% 50%;

        a,
        span {
            display: block;
            padding: 0;
            width: 2rem;
            height: 2rem;
        }
    }
    &--edit {
        background-image: url('#{$icons}/edit.svg');
        box-shadow: inset 0 0 0 1px $c-primary;
    }
    &--download {
        background-image: url('#{$icons}/download.svg');
        box-shadow: inset 0 0 0 1px $c-primary;
    }
    &--delete {
        background-image: url('#{$icons}/waste-bin.svg');
        box-shadow: inset 0 0 0 1px $c-red;
    }
    &--logout {
        background-color: $c-primary;
        background-image: url('#{$icons}/logout.svg');
    }
    &--info {
        background-color: $c-primary;
        background-image: url('#{$icons}/info.svg');
    }
}

</style>
